import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, rupiah } from "../lib/api";
import { FibiLoading } from "../components/FibiLoading";
import { useRouters } from "../context/RouterContext";
import { useAuth } from "../context/AuthContext";
import PinConfirm from "../components/PinConfirm";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AgentMutasiActions } from "../components/AgentMutasiActions";
import { ApplyTimeLimitButton } from "../components/ApplyTimeLimitButton";
import { ChargeReconcileButton } from "../components/ChargeReconcileButton";
import { PppReconcileButton } from "../components/PppReconcileButton";
import { PortalOrdersButton } from "../components/PortalOrdersButton";
import { RouterCombobox } from "../components/RouterCombobox";
import { NtaAuditDialog } from "../components/NtaAuditDialog";
import { DatePicker } from "../components/DatePicker";

const today = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10); // tanggal WIB
const PER_PAGE = 100;

export default function LaporanMikhmon({ type }) {
  const isVoucher = type === "voucher";
  const title = isVoucher ? "Transaksi Voucher" : "Transaksi PPPoE";
  const { routers, setSelected } = useRouters();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [revokeTarget, setRevokeTarget] = useState(null);
  const doRevoke = async () => {
    const r = revokeTarget;
    try {
      const { data } = await api.post("/vouchers/revoke-batch", { router_id: r.router_id, batch_id: r.batch_id });
      toast.success(`Batch ${r.batch_id} dicabut: ${data.deleted} kode dihapus dari sistem, ${data.removed} dihapus dari MikroTik${data.kicked ? `, ${data.kicked} sesi aktif diputus` : ""}${data.router_offline ? " (router offline — kode di MikroTik belum terhapus, ulangi saat online)" : ""}`, { duration: 12000 });
      setRevokeTarget(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const [cancelTarget, setCancelTarget] = useState(null);
  const doCancel = async (pin) => {
    const r = cancelTarget;
    try {
      const { data } = await api.post(`/reports/wifi-rumahan/${r.id}/cancel`, { pin, reason: "Klik ganda perpanjang" });
      toast.success(`Transaksi ${r.secret} dibatalkan${data.refunded ? ` — saldo ${data.refunded_to} dikembalikan ${rupiah(data.refunded)}` : ""}${data.expired_rolled_back ? `, expired kembali ke ${data.prev_expired_date || "belum di-setup"}` : ""}`, { duration: 12000 });
      setCancelTarget(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [auditOpen, setAuditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const qs = new URLSearchParams(window.location.search);
  const [f, setF] = useState({ keyword: "", from: qs.get("from") || today(), to: qs.get("to") || today(), router_id: "ALL", agent: "", profile: "ALL" });
  const [users, setUsers] = useState([]);
  const userByCode = Object.fromEntries(users.map((u) => [u.agent_code, u]).filter(([c]) => c));

  const load = useCallback(() => {
    setBusy(true);
    const params = {
      date_from: f.from || undefined, date_to: f.to || undefined,
      router_id: f.router_id === "ALL" ? undefined : f.router_id,
      agent: f.agent || undefined,
      ...(isVoucher ? { keyword: f.keyword || undefined, profile: f.profile === "ALL" ? undefined : f.profile } : {}),
    };
    api.get(isVoucher ? "/reports/voucher-wifi" : "/reports/wifi-rumahan", { params })
      .then((r) => { setData(r.data); setPage(0); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setBusy(false));
  }, [f, isVoucher]);

  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.rows || [];
  const pages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
  const cur = Math.min(page, pages - 1);
  const pageRows = rows.slice(cur * PER_PAGE, cur * PER_PAGE + PER_PAGE);
  const pager = rows.length > PER_PAGE && (
    <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-2 text-xs text-muted-foreground border-t border-border" data-testid="rep-pagination">
      <span>Menampilkan {cur * PER_PAGE + 1}–{Math.min(rows.length, (cur + 1) * PER_PAGE)} dari {rows.length} transaksi</span>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" className="h-7" disabled={cur === 0} onClick={() => setPage(cur - 1)} data-testid="rep-prev">‹ Sebelumnya</Button>
        <span className="px-2 font-mono">{cur + 1} / {pages}</span>
        <Button size="sm" variant="outline" className="h-7" disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)} data-testid="rep-next">Berikutnya ›</Button>
      </div>
    </div>
  );
  const openBatch = (row) => {
    setSelected(row.router_id);
    navigate(`/vouchers?router=${row.router_id}&batch=${row.batch_id}`);
  };

  return (
    <div className="space-y-5" data-testid={`laporan-${type}-page`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Printer className="w-7 h-7 text-[#F04E23]" /> {title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{isVoucher ? "Rekap voucher tercetak per batch di seluruh cabang." : "Rekap penjualan secret WiFi Rumahan (PPPoE) seluruh cabang."}</p>
        </div>
        {isVoucher && <div className="flex flex-wrap gap-2"><ChargeReconcileButton /><ApplyTimeLimitButton /></div>}
        {!isVoucher && <div className="flex flex-wrap gap-2"><PppReconcileButton dateFrom={f.from} dateTo={f.to} />{me?.role && ["admin", "admin_fasnet"].includes(me.role) && <PortalOrdersButton onChanged={load} autoOpen={qs.get("portal") === "1"} />}</div>}
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-4 grid grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          {isVoucher && (
            <div className="space-y-1 col-span-2 lg:col-span-1"><Label className="text-xs">Keyword (Paket Nama)</Label>
              <Input className="h-9 text-xs" placeholder="No Voucher (B2610...), kode voucher, atau paket" value={f.keyword} onChange={(e) => setF({ ...f, keyword: e.target.value })} data-testid="rep-keyword" /></div>
          )}
          <div className="space-y-1"><Label className="text-xs">Tgl Transaksi Awal</Label>
            <DatePicker className="h-9 text-xs" value={f.from} onChange={(v) => setF({ ...f, from: v })} testid="rep-date-from" /></div>
          <div className="space-y-1"><Label className="text-xs">Tgl Transaksi Akhir</Label>
            <DatePicker className="h-9 text-xs" value={f.to} onChange={(v) => setF({ ...f, to: v })} testid="rep-date-to" /></div>
          <div className="space-y-1"><Label className="text-xs">Router</Label>
            <RouterCombobox routers={routers} value={f.router_id} onChange={(v) => setF({ ...f, router_id: v })} allLabel="ALL" testid="rep-router" /></div>
          <div className="space-y-1"><Label className="text-xs">Agen</Label>
            <Input className="h-9 text-xs" placeholder="Kode Agen/Username/Nama" value={f.agent} onChange={(e) => setF({ ...f, agent: e.target.value })} data-testid="rep-agent" /></div>
          <div className="space-y-1 col-span-2 lg:col-span-1">
            <Button onClick={load} disabled={busy} className="w-full h-11 lg:h-9 text-sm bg-[#F04E23] hover:bg-[#d94420] active:scale-[0.98]" data-testid="rep-search">
              <Search className="w-4 h-4 mr-1.5" /> {busy ? "Memuat..." : "Cari"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-4 flex-wrap text-sm">
        {isVoucher ? (
          <>
            <span className="font-mono" data-testid="rep-total-voucher">Total Voucher Tercetak: <span className="font-bold text-sky-400">{(data?.total_voucher || 0).toLocaleString("id-ID")}</span></span>
            <span className="font-mono" data-testid="rep-total-modal">Total Harga NTA: <span className="font-bold text-amber-400">{rupiah(data?.total_modal || 0)},-</span></span>
            {data?.audit && (
              <button type="button" onClick={() => setAuditOpen(true)} className={`font-mono text-xs px-2 py-1 rounded-md border text-left hover:opacity-80 ${data.audit.diff === 0 ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : "border-rose-500/40 text-rose-400 bg-rose-500/10"}`}
                title="Cek silang: NTA batch milik agen dibandingkan saldo agen yang benar-benar terpotong (Cetak vcr − Refund + Koreksi) pada rentang tanggal yang sama. Klik untuk rincian per agen." data-testid="rep-audit">
                Saldo agen terpotong: {rupiah(data.audit.charged_net)},- {data.audit.nta_admin > 0 && <>• NTA admin: {rupiah(data.audit.nta_admin)},- </>}
                {data.audit.diff === 0 ? "• ✓ cocok" : `• selisih ${data.audit.diff > 0 ? "+" : ""}${rupiah(data.audit.diff)},- → klik untuk rincian per agen`}
              </button>
            )}
            <span className="flex items-center gap-2 ml-auto"><Label className="text-xs">Tipe Voucher</Label>
              <Select value={f.profile} onValueChange={(v) => setF({ ...f, profile: v })}>
                <SelectTrigger className="h-8 w-40 text-xs" data-testid="rep-profile"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL" className="text-xs">ALL</SelectItem>
                  {(data?.profiles || []).map((p) => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}
                </SelectContent>
              </Select></span>
          </>
        ) : (
          <>
            <span className="font-mono" data-testid="rep-total-terjual">Total Terjual: <span className="font-bold text-sky-400">{data?.total_terjual || 0}</span></span>
            <span className="font-mono" data-testid="rep-total-harga">Total Harga: <span className="font-bold text-amber-400">{rupiah(data?.total_harga || 0)},-</span></span>
          </>
        )}
      </div>

      <Card className="bg-card border-border shadow-sm overflow-x-auto">
        <CardHeader><CardTitle className="text-base font-heading">{title}</CardTitle></CardHeader>
        <Table className="min-w-[960px]" data-testid="rep-table">
          <TableHeader>
            {isVoucher ? (
              <TableRow>
                <TableHead className="w-10">#</TableHead><TableHead>No Voucher</TableHead><TableHead>Created Date</TableHead>
                <TableHead>Router</TableHead><TableHead>Nama Paket</TableHead><TableHead className="text-right">Jumlah Voucher</TableHead><TableHead className="text-right" title="Jumlah kode nyata di MikroTik saat terakhir dicek (otomatis 10 mnt setelah cetak / saat batch dibuka)">Di Router</TableHead>
                <TableHead className="text-right">Harga Modal</TableHead><TableHead>Agen</TableHead><TableHead className="text-right">Mutasi Saldo</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow>
            ) : (
              <TableRow>
                <TableHead className="w-10">#</TableHead><TableHead>Created/Expired Date</TableHead><TableHead>Router</TableHead>
                <TableHead>Profile Name</TableHead><TableHead>Secret Name</TableHead><TableHead>Expired</TableHead>
                <TableHead className="text-right">Harga</TableHead><TableHead>Agen</TableHead><TableHead className="text-right">Mutasi Saldo</TableHead>
              </TableRow>
            )}
          </TableHeader>
          <TableBody>
            {!rows.length && <TableRow><TableCell colSpan={isVoucher ? 11 : 9} className={busy ? "p-0" : "text-center py-8 text-muted-foreground"} data-testid="rep-empty">{busy ? <FibiLoading text="FIBI sedang memuat laporan..." size={20} /> : "Tidak ada data pada filter ini"}</TableCell></TableRow>}
            {pageRows.map((r, i) => isVoucher ? (
              <TableRow key={`${r.router_id}-${r.batch_id}`} data-testid={`rep-row-${r.batch_id}`}>
                <TableCell className="text-xs text-muted-foreground">{cur * PER_PAGE + i + 1}</TableCell>
                <TableCell className="font-mono text-xs font-bold text-sky-400">{r.batch_id}</TableCell>
                <TableCell className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">{fmtDateTime(r.created_at)}</TableCell>
                <TableCell className="text-xs font-medium">{r.router_name}</TableCell>
                <TableCell className="text-xs">{r.profile}</TableCell>
                <TableCell className="text-right font-mono text-xs">{r.count}</TableCell>
                <TableCell className="text-right font-mono text-xs" data-testid={`rep-onrouter-${r.batch_id}`} title={r.on_router_at ? `Dicek ${fmtDateTime(r.on_router_at)}` : "Belum dicek — buka batch untuk mengecek"}>
                  {r.on_router == null ? <span className="text-muted-foreground">—</span> : r.on_router < r.count ? <span className="text-rose-400 font-bold">{r.on_router} <span className="text-[10px] font-normal">(−{r.count - r.on_router})</span></span> : <span className="text-emerald-400">{r.on_router}</span>}
                </TableCell>
                <TableCell className="text-right font-mono text-xs text-amber-400">{rupiah(r.modal)},-</TableCell>
                <TableCell className="text-xs font-mono text-muted-foreground">{r.agen}</TableCell>
                <TableCell className="text-right">
                  <AgentMutasiActions agent={{ code: r.agent_code, name: r.agent_name || r.agen }} user={userByCode[r.agent_code]} testid={`mutasi-v-${r.batch_id}`} />
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <a href={`/vouchers?router=${r.router_id}&batch=${r.batch_id}`} onClick={(e) => { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return; e.preventDefault(); openBatch(r); }}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-emerald-400 hover:bg-accent" title="Buka batch voucher (klik kanan → buka di tab baru)" data-testid={`rep-open-${r.batch_id}`}><Printer className="w-4 h-4" /></a>
                  {me?.role === "admin" && <Button size="icon" variant="ghost" className="text-rose-400 h-8 w-8" title="Cabut batch: hapus semua kode dari MikroTik & sistem" onClick={() => setRevokeTarget(r)} data-testid={`rep-revoke-${r.batch_id}`}><Trash2 className="w-4 h-4" /></Button>}
                </TableCell>
              </TableRow>
            ) : (
              <TableRow key={r.id} data-testid={`rep-row-${r.id}`}>
                <TableCell className="text-xs text-muted-foreground">{cur * PER_PAGE + i + 1}</TableCell>
                <TableCell className="text-[11px] font-mono whitespace-nowrap">{fmtDateTime(r.created_at)}<div className="text-rose-400">{r.expired !== "-" ? `Exp: ${fmtDate(r.expired)}` : "-"}</div></TableCell>
                <TableCell className="text-xs font-medium">{r.router_name}</TableCell>
                <TableCell className="text-xs">{r.profile}</TableCell>
                <TableCell className="text-xs font-mono">
                  <button type="button" className="text-sky-400 hover:underline text-left" title={`Buka secret di cabang ${r.router_name}`} onClick={() => navigate(`/ppp?router=${encodeURIComponent(r.router_id || "")}&secret=${encodeURIComponent(r.secret)}`)} data-testid={`rep-secret-link-${r.id}`}>{r.secret}</button>
                </TableCell>
                <TableCell className="text-xs font-mono text-rose-400 whitespace-nowrap" data-testid={`rep-exp-${r.id}`}>{r.expired !== "-" ? fmtDate(r.expired) : "-"}</TableCell>
                <TableCell className="text-right font-mono text-xs text-amber-400">{rupiah(r.harga)},-</TableCell>
                <TableCell className="text-xs font-mono">
                  <div className="text-muted-foreground">{r.agen}</div>
                  {r.mutasi && (
                    <div className={`text-[10px] mt-0.5 whitespace-nowrap ${r.mutasi.status === "potong" ? "text-emerald-400" : r.mutasi.status === "admin" ? "text-sky-400" : "text-rose-400"}`} title={`${r.mutasi.note}${r.mutasi.email ? ` • ${r.mutasi.email}` : ""}`} data-testid={`rep-mutasi-${r.id}`}>
                      {r.mutasi.status === "potong" ? `✓ Saldo terpotong ${rupiah(r.mutasi.amount)}` : r.mutasi.status === "admin" ? "Admin — tidak memotong saldo" : "⚠ Tidak memotong saldo"}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <AgentMutasiActions agent={{ code: r.agent_code, name: r.agent_name || r.agen }} user={userByCode[r.agent_code]} testid={`mutasi-p-${r.id}`} />
                  {me?.role === "admin" && r.mutasi?.status !== "admin" && <Button size="icon" variant="ghost" className="text-rose-400 h-8 w-8" title="Batalkan transaksi (klik ganda): saldo agen dikembalikan, expired dikembalikan" onClick={() => setCancelTarget(r)} data-testid={`rep-cancel-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {pager}
      </Card>
      <NtaAuditDialog open={auditOpen} onClose={() => setAuditOpen(false)} dateFrom={f.from} dateTo={f.to} />
      <PinConfirm open={!!revokeTarget} onOpenChange={(v) => { if (!v) setRevokeTarget(null); }} destructive confirmLabel="Cabut & Hapus"
        title={`Cabut batch ${revokeTarget?.batch_id}?`}
        description={`${revokeTarget?.count} kode ${revokeTarget?.profile} milik ${revokeTarget?.agen} di ${revokeTarget?.router_name} akan DIHAPUS dari MikroTik & sistem, sesi aktif diputus. Tidak bisa dibatalkan.`}
        onConfirm={doRevoke} testid="pin-revoke-batch" />
      <PinConfirm open={!!cancelTarget} onOpenChange={(v) => { if (!v) setCancelTarget(null); }} destructive confirmLabel="Batalkan & Refund"
        title={`Batalkan perpanjang ${cancelTarget?.secret}?`}
        description={`Transaksi ${cancelTarget ? fmtDateTime(cancelTarget.created_at) : ""} senilai ${rupiah(cancelTarget?.harga || 0)} oleh ${cancelTarget?.agen} akan dibatalkan. ${cancelTarget?.charged > 0 ? `Saldo agen dikembalikan ${rupiah(cancelTarget.charged)}` : "Tidak ada saldo agen yang perlu dikembalikan"}; tanggal expired dikembalikan ke sebelum transaksi ini (jika belum berubah). Agen menerima pesan sistem.`}
        onConfirm={doCancel} testid="pin-cancel-ppp" />
    </div>
  );
}
